import { useCallback } from "react";
import { isBitSet } from "../../utils";
import EntityCalendar from "../../components/EntityCalendar";

const SHIFT_COLORS = [
    "#2563EB",
    "#22C55E",
    "#F59E0B",
    "#EF4444",
    "#A855F7",
    "#0EA5E9",
    "#14B8A6",
    "#E11D48",
    "#F97316",
    "#84CC16",
    "#06B6D4",
    "#D946EF",
];

function hashId(id) {
    if (typeof id === "number") return id;
    return String(id)
        .split("")
        .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
}
function pickBgColor(id) {
    return SHIFT_COLORS[hashId(id) % SHIFT_COLORS.length];
}
function makeLocalDate(baseDay, sec) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return new Date(
        baseDay.getFullYear(),
        baseDay.getMonth(),
        baseDay.getDate(),
        h,
        m,
        0,
        0,
    );
}
function endOfDayLocal(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 0);
}
function addDaysLocal(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}

const generateEvents = (dateRange, shifts) => {
    if (!dateRange || !dateRange.start || !dateRange.end || !shifts?.length)
        return [];

    const events = [];
    const oneDay = 24 * 60 * 60 * 1000;

    for (const s of shifts) {
        const {
            id,
            name,
            start_time: st = 0,
            end_time: et = 0,
            runs_on,
            created_at,
        } = s;
        const bg = s.is_active === false ? "#adb5bd" : pickBgColor(id);
        // eventDidMount paints only a light tint of `bg` behind the event, so the text
        // takes the solid shift colour; a contrast colour (often white) would vanish.
        const fg = s.is_active === false ? "#6c757d" : bg;
        const shiftCreatedAt = created_at ? new Date(created_at) : new Date(0);
        shiftCreatedAt.setHours(0, 0, 0, 0);
        // Start checking 1 day before the view starts, so we catch shifts that
        // began yesterday and cross midnight into the current view.
        let current = addDaysLocal(new Date(dateRange.start), -1);
        while (current < dateRange.end) {
            if (current >= shiftCreatedAt) {
                const dayIndex = current.getDay();

                if (isBitSet(runs_on, dayIndex)) {
                    const dayStart = new Date(current);
                    const crossesMidnight = st >= et;

                    if (!crossesMidnight) {
                        events.push({
                            id: `${id}-${dayStart.getTime()}-single`,
                            title: name || "Shift",
                            start: makeLocalDate(dayStart, st),
                            end: makeLocalDate(dayStart, et),
                            extendedProps: { shift: s },
                            color: bg,
                            textColor: fg,
                        });
                    } else {
                        events.push({
                            id: `${id}-${dayStart.getTime()}-A`,
                            title: name || "Shift",
                            start: makeLocalDate(dayStart, st),
                            end: endOfDayLocal(dayStart),
                            extendedProps: { shift: s, overnight: true },
                            color: bg,
                            textColor: fg,
                        });
                        const nextDay = addDaysLocal(dayStart, 1);
                        events.push({
                            id: `${id}-${dayStart.getTime()}-B`,
                            title: name,
                            start: makeLocalDate(nextDay, 0),
                            end: makeLocalDate(nextDay, et),
                            extendedProps: { shift: s, overnight: true },
                            color: bg,
                            textColor: fg,
                        });
                    }
                }
            }
            current = new Date(current.getTime() + oneDay);
        }
    }
    return events;
};

const ShiftWeekCalendar = ({ shifts, onEventClick }) => {
    // Stable per shifts list: FullCalendar re-reads the source only when this identity
    // changes, so no manual refetchEvents is needed.
    const fetchEvents = useCallback(
        (info, successCallback) => {
            successCallback(
                generateEvents({ start: info.start, end: info.end }, shifts),
            );
        },
        [shifts],
    );

    return (
        <EntityCalendar
            events={fetchEvents}
            eventClick={(info) => onEventClick?.(info.event.extendedProps.shift)}
        />
    );
};

export default ShiftWeekCalendar;
